import { useCallback, useEffect, useRef } from 'react';
import { dbui_v1_Query_LookupResult } from '@antimatterhq/asl-api-ts-v1';
import { useAtomCallback } from 'jotai/utils';
import { useAtomValue } from 'jotai';
import { ListRange } from 'react-virtuoso';
import { useDebouncedCallback } from 'use-debounce';
import {
  clearQueryTableStateAtomFamily,
  QueryResultRow,
  queryTableStateAtomFamily,
} from '@/atoms/query/queryTableResultsAtoms';
import { useGetQueryLookupImperative } from '@/hooks/dbui/useGetQueryLookup';
import {
  queryTabAtomFamily,
  updateTabAtomFamily,
} from '@/atoms/query/queryAtoms';
import { queryStartValueAtomFamily } from '@/atoms/query/queryResultsAtoms';
import { useWorkspace } from '@/contexts/WorkspaceContext';

// Helper function to build stable ID (moved here to be used at load time)
export const buildStableId = (r: Record<string, string>): string => {
  if (r.dasl_id) return `row-${r.dasl_id}`;

  const str = JSON.stringify(r);
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    const chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }

  return `row-${hash}`;
};

// Transform raw API data to our internal format
export const transformResultData = (
  row: Record<string, string>,
): QueryResultRow => {
  if (typeof row !== 'object') {
    throw new Error('Row is not an object');
  }

  const transformedRow: QueryResultRow = {
    id: buildStableId(row),
  };

  Object.entries(row).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      transformedRow[key] = '';
    } else if (typeof value === 'object') {
      transformedRow[key] = JSON.stringify(value);
    } else {
      transformedRow[key] = value;
    }
  });

  return transformedRow;
};

const PAGE_SIZE = 100;

// Build next/prev tokens directly from the raw API page so that we can pass
// these helper functions to `getNextPageParam` / `getPreviousPageParam`.
function buildNextPageParam(page: dbui_v1_Query_LookupResult | undefined) {
  if (!page?.data?.length) return null;

  const cols = page.paginationColumns?.length
    ? page.paginationColumns
    : ['time', '_row_number'];
  const lastRow = page.data[page.data.length - 1];
  const fields = Object.fromEntries(
    cols.map((c) => [c, String(lastRow[c] ?? '')]),
  );

  return {
    fields,
    fetchPreceding: false,
  };
}

function buildPrevPageParam(page: dbui_v1_Query_LookupResult | undefined) {
  if (!page?.data?.length) return null;

  const cols = page.paginationColumns?.length
    ? page.paginationColumns
    : ['time', '_row_number'];

  const firstRow = page.data[0];
  const fields = Object.fromEntries(
    cols.map((c) => [c, String(firstRow[c] ?? '')]),
  );

  return {
    fields,
    fetchPreceding: true,
  };
}

export function useQueryResults(tabId: string) {
  const { workspace } = useWorkspace();
  const { mutateAsync: fatchPageAsync } = useGetQueryLookupImperative();
  const startValue = useAtomValue(queryStartValueAtomFamily(tabId));
  const previousStartValueRef = useRef(startValue);

  const fetchInitialPage = useAtomCallback(
    useCallback(
      async (get, set) => {
        const tab = get(queryTabAtomFamily(tabId));
        const { metadata } = get(queryTableStateAtomFamily(tabId));

        if (metadata.isLoadingInitialPage) return;

        set(queryTableStateAtomFamily(tabId), (draft) => {
          draft.metadata.isLoadingInitialPage = true;
          draft.metadata.isLoadingNextPage = false;
          draft.metadata.isLoadingPreviousPage = false;
        });

        await fatchPageAsync(
          {
            path: {
              workspace: workspace ?? '',
              id: tab?.queryId ?? '',
            },
            body: {
              rowCount: PAGE_SIZE,
              refinements: tab?.refinements ?? [],
              startValue: get(queryStartValueAtomFamily(tabId)),
            },
          },
          {
            onSuccess: async ({ data }) => {
              const queryResultRows = data?.data.map(transformResultData);

              // Check if no data was returned on initial load
              if (data?.data.length === 0 && data?.columns?.length === 0) {
                // Set query status back to idle to show "no results" state
                set(updateTabAtomFamily(tabId), { status: 'idle' });
              }

              set(queryTableStateAtomFamily(tabId), (draft) => {
                draft.metadata.hasNextPage = data?.data.length === PAGE_SIZE;
                draft.metadata.hasPreviousPage = true;
                draft.metadata.nextPageParams = buildNextPageParam(data);
                draft.metadata.previousPageParams = buildPrevPageParam(data);
                draft.rows = queryResultRows;
                draft.metadata.isLoadingInitialPage = false;
              });
            },
            onError: async ({ error }) => {
              set(queryTableStateAtomFamily(tabId), (draft) => {
                draft.metadata.isLoadingInitialPage = false;
                draft.metadata.error = error;
              });
            },
          },
        );
      },
      [fatchPageAsync, workspace, tabId],
    ),
  );

  const fetchNextPage = useAtomCallback(
    useCallback(
      async (get, set) => {
        const tab = get(queryTabAtomFamily(tabId));
        const { metadata } = get(queryTableStateAtomFamily(tabId));
        const { nextPageParams, hasNextPage, isLoadingNextPage } = metadata;

        if (!hasNextPage || !nextPageParams || isLoadingNextPage) {
          return;
        }

        set(queryTableStateAtomFamily(tabId), (draft) => {
          draft.metadata.isLoadingNextPage = true;
        });

        await fatchPageAsync(
          {
            path: {
              workspace: workspace ?? '',
              id: tab?.queryId ?? '',
            },
            body: {
              rowCount: PAGE_SIZE,
              refinements: tab?.refinements ?? [],
              pagination: nextPageParams,
            },
          },
          {
            onSuccess: async ({ data }) => {
              const queryResultRows = data?.data.map(transformResultData);
              set(queryTableStateAtomFamily(tabId), (draft) => {
                draft.metadata.hasNextPage = data?.data.length === PAGE_SIZE;
                draft.metadata.nextPageParams = buildNextPageParam(data);
                draft.rows.push(...queryResultRows);
                draft.metadata.isLoadingNextPage = false;
              });
            },
            onError: async ({ error }) => {
              set(queryTableStateAtomFamily(tabId), (draft) => {
                draft.metadata.isLoadingNextPage = false;
                draft.metadata.error = error;
              });
            },
          },
        );
      },
      [tabId, fatchPageAsync, workspace],
    ),
  );

  const fetchPreviousPage = useAtomCallback(
    useCallback(
      async (get, set) => {
        const tab = get(queryTabAtomFamily(tabId));
        const { metadata } = get(queryTableStateAtomFamily(tabId));
        const { previousPageParams, hasPreviousPage, isLoadingPreviousPage } =
          metadata;

        if (
          !hasPreviousPage ||
          !previousPageParams ||
          isLoadingPreviousPage ||
          !get(queryStartValueAtomFamily(tabId))
        ) {
          return;
        }

        set(queryTableStateAtomFamily(tabId), (draft) => {
          draft.metadata.isLoadingPreviousPage = true;
        });

        await fatchPageAsync(
          {
            path: {
              workspace: workspace ?? '',
              id: tab?.queryId ?? '',
            },
            body: {
              rowCount: PAGE_SIZE,
              refinements: tab?.refinements ?? [],
              pagination: previousPageParams,
            },
          },
          {
            onSuccess: async ({ data }) => {
              const queryResultRows = data?.data.map(transformResultData);
              set(queryTableStateAtomFamily(tabId), (draft) => {
                draft.metadata.hasPreviousPage =
                  data?.data.length === PAGE_SIZE;
                draft.metadata.previousPageParams = buildPrevPageParam(data);
                draft.rows.unshift(...queryResultRows);
                draft.metadata.isLoadingPreviousPage = false;
                draft.metadata.firstItemIndex =
                  draft.metadata.firstItemIndex - (data?.data.length ?? 0);
              });
            },
            onError: async ({ error }) => {
              set(queryTableStateAtomFamily(tabId), (draft) => {
                draft.metadata.isLoadingPreviousPage = false;
                draft.metadata.error = error;
              });
            },
          },
        );
      },
      [tabId, fatchPageAsync, workspace],
    ),
  );

  const clearQueryTableResultsAndRefetch = useAtomCallback(
    useCallback(
      async (_get, set) => {
        set(clearQueryTableStateAtomFamily(tabId));
        await fetchInitialPage();
        await fetchPreviousPage();
      },
      [fetchInitialPage, fetchPreviousPage, tabId],
    ),
  );

  useEffect(() => {
    if (previousStartValueRef.current !== startValue) {
      clearQueryTableResultsAndRefetch();
    }
    previousStartValueRef.current = startValue;
  }, [startValue, clearQueryTableResultsAndRefetch]);

  const tab = useAtomValue(queryTabAtomFamily(tabId));
  const previousRefinementsRef = useRef(tab?.refinements);

  // Watch for refinement changes
  useEffect(() => {
    const refinementsChanged =
      JSON.stringify(previousRefinementsRef.current) !==
      JSON.stringify(tab?.refinements);
    if (refinementsChanged && previousRefinementsRef.current !== undefined) {
      clearQueryTableResultsAndRefetch();
    }
    previousRefinementsRef.current = tab?.refinements;
  }, [tab?.refinements, clearQueryTableResultsAndRefetch]);

  const { rows, metadata } = useAtomValue(queryTableStateAtomFamily(tabId));
  const {
    hasNextPage,
    hasPreviousPage,
    isLoadingInitialPage,
    isLoadingNextPage,
    isLoadingPreviousPage,
    firstItemIndex,
  } = metadata;

  useEffect(() => {
    fetchInitialPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRangeChangedDebounced = useDebouncedCallback(
    (range: ListRange) => {
      console.warn('handleRangeChangedDebounced', range);
    },
    1000,
  );

  const handleRangeChanged = useCallback(
    (range: ListRange) => {
      handleRangeChangedDebounced(range);
    },
    [handleRangeChangedDebounced],
  );

  return {
    fetchInitialPage,
    fetchNextPage,
    fetchPreviousPage,
    isLoadingInitialPage,
    isLoadingNextPage,
    isLoadingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    rows,
    firstItemIndex,
    clearQueryTableResultsAndRefetch,
    handleRangeChanged,
  };
}
